import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import useAdminData from '../components/shared/useAdminData';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import Modal from '../components/common/Modal';
import StatusBadge from '../components/common/StatusBadge';
import SearchBar from '../components/common/SearchBar';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './FeatureFlags.css';

const { Skeleton } = SkeletonLoaderComponents;

const FeatureFlags = () => {
  const { user } = useAdminAuth();
  const [flags, setFlags] = useState([]);
  const [filteredFlags, setFilteredFlags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const { data, loading, error: fetchError, refetch } = useAdminData(
    'feature-flags',
    `${API_ENDPOINTS.ADMIN.FEATURE_FLAGS}/flags`,
    { ttl: 30000 }
  );

  useEffect(() => {
    if (data?.flags) {
      setFlags(data.flags);
      setFilteredFlags(data.flags);
    }
  }, [data]);

  useEffect(() => {
    if (fetchError) {
      setError(fetchError || 'Failed to load feature flags');
    }
  }, [fetchError]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredFlags(flags);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = flags.filter(flag =>
        flag.name.toLowerCase().includes(query) ||
        flag.flagId.toLowerCase().includes(query) ||
        flag.description?.toLowerCase().includes(query)
      );
      setFilteredFlags(filtered);
    }
  }, [searchQuery, flags]);

  const handleCreateFlag = async (flagData) => {
    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      const response = await fetch(`${API_ENDPOINTS.ADMIN.FEATURE_FLAGS}/flags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(flagData)
      });

      if (!response.ok) {
        throw new Error('Failed to create feature flag');
      }

      setSuccessMessage('Feature flag created successfully');
      setShowCreateModal(false);
      refetch();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleUpdateFlag = async (flagId, updates) => {
    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      const response = await fetch(`${API_ENDPOINTS.ADMIN.FEATURE_FLAGS}/flags/${flagId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update feature flag');
      }

      setSuccessMessage('Feature flag updated successfully');
      setShowEditModal(false);
      setSelectedFlag(null);
      refetch();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleToggleFlag = async (flag) => {
    await handleUpdateFlag(flag.flagId, { enabled: !flag.enabled });
  };

  const handleDeleteFlag = async (flagId) => {
    if (!window.confirm('Are you sure you want to delete this feature flag?')) {
      return;
    }

    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      const response = await fetch(`${API_ENDPOINTS.ADMIN.FEATURE_FLAGS}/flags/${flagId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete feature flag');
      }

      setSuccessMessage('Feature flag deleted successfully');
      refetch();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="feature-flags">
        <div className="feature-flags__header">
          <h1>Feature Flags</h1>
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} width="100%" height="80px" className="admin-skeleton-gap" />
        ))}
      </div>
    );
  }

  return (
    <div className="feature-flags">
      <div className="feature-flags__header">
        <h1>Feature Flags</h1>
        <button
          className="feature-flags__create-btn"
          onClick={() => setShowCreateModal(true)}
        >
          Create Feature Flag
        </button>
      </div>

      {error && (
        <div className="feature-flags__alert feature-flags__alert--error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="feature-flags__alert feature-flags__alert--success">
          {successMessage}
        </div>
      )}

      <div className="feature-flags__controls">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search flags by name, ID, or description..."
        />
      </div>

      <div className="feature-flags__list">
        {filteredFlags.length === 0 ? (
          <div className="feature-flags__empty">
            {searchQuery ? 'No flags match your search' : 'No feature flags created yet'}
          </div>
        ) : (
          filteredFlags.map(flag => (
            <div key={flag.flagId} className="feature-flag-card">
              <div className="feature-flag-card__header">
                <div className="feature-flag-card__title">
                  <h3>{flag.name}</h3>
                  <span className="feature-flag-card__id">{flag.flagId}</span>
                </div>
                <div className="feature-flag-card__status">
                  <StatusBadge
                    status={flag.enabled ? 'active' : 'inactive'}
                    label={flag.enabled ? 'Enabled' : 'Disabled'}
                  />
                </div>
              </div>

              {flag.description && (
                <p className="feature-flag-card__description">{flag.description}</p>
              )}

              <div className="feature-flag-card__details">
                <div className="feature-flag-card__detail">
                  <span className="feature-flag-card__label">Target Type:</span>
                  <span className="feature-flag-card__value">{flag.targetType}</span>
                </div>
                {flag.targetValue && (
                  <div className="feature-flag-card__detail">
                    <span className="feature-flag-card__label">Target Value:</span>
                    <span className="feature-flag-card__value">{flag.targetValue}</span>
                  </div>
                )}
                <div className="feature-flag-card__detail">
                  <span className="feature-flag-card__label">Last Modified:</span>
                  <span className="feature-flag-card__value">
                    {new Date(flag.updatedAt).toLocaleString()}
                  </span>
                </div>
              </div>

              {flag.variants && flag.variants.length > 0 && (
                <div className="feature-flag-card__variants">
                  <h4>A/B Test Variants:</h4>
                  <div className="feature-flag-card__variants-list">
                    {flag.variants.map(variant => (
                      <div key={variant.id} className="variant-item">
                        <span className="variant-item__name">{variant.name}</span>
                        <span className="variant-item__weight">{variant.weight}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="feature-flag-card__actions">
                <button
                  className={`feature-flag-card__toggle ${flag.enabled ? 'feature-flag-card__toggle--enabled' : ''}`}
                  onClick={() => handleToggleFlag(flag)}
                >
                  {flag.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  className="feature-flag-card__edit"
                  onClick={() => {
                    setSelectedFlag(flag);
                    setShowEditModal(true);
                  }}
                >
                  Edit
                </button>
                <button
                  className="feature-flag-card__delete"
                  onClick={() => handleDeleteFlag(flag.flagId)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <FlagFormModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateFlag}
          title="Create Feature Flag"
        />
      )}

      {showEditModal && selectedFlag && (
        <FlagFormModal
          onClose={() => {
            setShowEditModal(false);
            setSelectedFlag(null);
          }}
          onSubmit={(data) => handleUpdateFlag(selectedFlag.flagId, data)}
          title="Edit Feature Flag"
          initialData={selectedFlag}
        />
      )}
    </div>
  );
};

const FlagFormModal = ({ onClose, onSubmit, title, initialData = null }) => {
  const [formData, setFormData] = useState({
    flagId: initialData?.flagId || '',
    name: initialData?.name || '',
    description: initialData?.description || '',
    enabled: initialData?.enabled ?? true,
    targetType: initialData?.targetType || 'all',
    targetValue: initialData?.targetValue || '',
    variants: initialData?.variants || []
  });

  const [newVariant, setNewVariant] = useState({ id: '', name: '', weight: 0 });
  const [formError, setFormError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.name.trim()) {
      setFormError('Flag name is required');
      return;
    }

    if (!initialData && !formData.flagId.trim()) {
      setFormError('Flag ID is required');
      return;
    }

    if (formData.targetType !== 'all' && !formData.targetValue) {
      setFormError('Target value is required for non-all target types');
      return;
    }

    if (formData.variants.length > 0) {
      const totalWeight = formData.variants.reduce((sum, v) => sum + Number(v.weight), 0);
      if (Math.abs(totalWeight - 100) > 0.01) {
        setFormError('Variant weights must sum to 100%');
        return;
      }
    }

    onSubmit(formData);
  };

  const handleAddVariant = () => {
    if (!newVariant.id || !newVariant.name || newVariant.weight <= 0) {
      setFormError('All variant fields are required');
      return;
    }

    setFormData({
      ...formData,
      variants: [...formData.variants, { ...newVariant }]
    });
    setNewVariant({ id: '', name: '', weight: 0 });
    setFormError(null);
  };

  const handleRemoveVariant = (variantId) => {
    setFormData({
      ...formData,
      variants: formData.variants.filter(v => v.id !== variantId)
    });
  };

  return (
    <Modal onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="flag-form">
        {formError && (
          <div className="flag-form__error">{formError}</div>
        )}

        <div className="flag-form__field">
          <label htmlFor="flagId">Flag ID (kebab-case)</label>
          <input
            type="text"
            id="flagId"
            value={formData.flagId}
            onChange={(e) => setFormData({ ...formData, flagId: e.target.value })}
            disabled={!!initialData}
            placeholder="feature-name"
            required
          />
        </div>

        <div className="flag-form__field">
          <label htmlFor="name">Display Name</label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Feature Name"
            required
          />
        </div>

        <div className="flag-form__field">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe what this flag controls..."
            rows={3}
          />
        </div>

        <div className="flag-form__field">
          <label>
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>

        <div className="flag-form__field">
          <label htmlFor="targetType">Target Type</label>
          <select
            id="targetType"
            value={formData.targetType}
            onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
          >
            <option value="all">All Users</option>
            <option value="user">Specific User ID</option>
            <option value="group">Cognito Group</option>
            <option value="percentage">Percentage Rollout</option>
            <option value="region">Geographic Region</option>
          </select>
        </div>

        {formData.targetType !== 'all' && (
          <div className="flag-form__field">
            <label htmlFor="targetValue">Target Value</label>
            <input
              type="text"
              id="targetValue"
              value={formData.targetValue}
              onChange={(e) => setFormData({ ...formData, targetValue: e.target.value })}
              placeholder={
                formData.targetType === 'user' ? 'user-id' :
                formData.targetType === 'group' ? 'Admins' :
                formData.targetType === 'percentage' ? '50' :
                'ap-southeast-1'
              }
            />
          </div>
        )}

        <div className="flag-form__section">
          <h4>A/B Test Variants (Optional)</h4>
          
          {formData.variants.length > 0 && (
            <div className="flag-form__variants-list">
              {formData.variants.map(variant => (
                <div key={variant.id} className="variant-item">
                  <span>{variant.name} ({variant.weight}%)</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveVariant(variant.id)}
                    className="variant-item__remove"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flag-form__variant-input">
            <input
              type="text"
              placeholder="Variant ID"
              value={newVariant.id}
              onChange={(e) => setNewVariant({ ...newVariant, id: e.target.value })}
            />
            <input
              type="text"
              placeholder="Variant Name"
              value={newVariant.name}
              onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
            />
            <input
              type="number"
              placeholder="Weight %"
              value={newVariant.weight}
              onChange={(e) => setNewVariant({ ...newVariant, weight: Number(e.target.value) })}
              min="0"
              max="100"
            />
            <button type="button" onClick={handleAddVariant}>
              Add Variant
            </button>
          </div>
        </div>

        <div className="flag-form__actions">
          <button type="button" onClick={onClose} className="flag-form__cancel">
            Cancel
          </button>
          <button type="submit" className="flag-form__submit">
            {initialData ? 'Update Flag' : 'Create Flag'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default FeatureFlags;
